/**
 * Standings sorting utilities
 * 
 * Tiebreaker rules (in order):
 * 1. Points (descending) - most points wins
 * 2. Wins (descending) - most wins wins
 * 3. Losses (ascending) - fewest losses wins
 * 4. Name (alphabetical) - for consistent ordering
 * 
 * IMPORTANT: This logic is also implemented server-side in:
 * - supabase/functions/get-standings-split/index.ts (toArray function)
 * 
 * If tiebreaker rules change, update BOTH locations.
 */

export interface StandingsSortable {
  pts: number;
  w: number;
  l: number;
  name?: string | null;
}

/**
 * Compare function for sorting standings with tiebreakers.
 * Use with Array.sort(): standings.sort(compareStandings)
 */
export function compareStandings<T extends StandingsSortable>(a: T, b: T): number {
  // 1. Points descending (most points wins)
  if (b.pts !== a.pts) return b.pts - a.pts;
  
  // 2. Wins descending (most wins wins)
  if (b.w !== a.w) return b.w - a.w;
  
  // 3. Losses ascending (fewest losses wins)
  if (a.l !== b.l) return a.l - b.l;
  
  // 4. Name alphabetical (for consistent ordering)
  return (a.name || '').localeCompare(b.name || '');
}

/**
 * Sort an array of standings in place using tiebreaker rules.
 * Returns the same array for chaining.
 */
export function sortStandings<T extends StandingsSortable>(standings: T[]): T[] {
  return standings.sort(compareStandings);
}



